import { useParams } from "react-router";
import { useOutletContext } from "react-router-dom";

import {
  Paper,
  Stack,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
} from "@mui/material";

import Loader from "../../components/Loader";
import useGetVisitStatusSummary from "../../hooks/useGetVisitStatusSummary";
import { formatDate } from "../../utils/helpers";

const ManuallyMarkVisit = () => {
  const { _id_ } = useParams();

  const { trialFilter, setActive, setBreadcrumbTitle } = useOutletContext() ?? {};

  setActive("status");
  setBreadcrumbTitle("Visit Status");

  const { data: summaryData, isLoading } = useGetVisitStatusSummary({
    patientId: _id_,
    trialId: trialFilter,
    enabled: !!trialFilter,
  });

  const sessions = summaryData?.sessions || [];
  const hasAnyInAppActivity = summaryData?.has_any_inapp_activity || false;
  const hasAnyInClinicActivity = summaryData?.has_any_inclinic_activity || false;

  const headerCellSx = {
    backgroundColor: (theme) => theme.palette.grey[100],
    fontWeight: "bold",
    textTransform: "uppercase",
    fontSize: "0.85rem",
    color: "#424242",
    whiteSpace: "nowrap",
  };

  const bodyCellSx = {
    whiteSpace: "nowrap",
  };

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      {/* Header */}

      <Stack spacing={2} mb={2}>
        <Typography variant="h6">Visit Status</Typography>
      </Stack>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Visit Name</TableCell>
              <TableCell sx={headerCellSx}>Visit Type</TableCell>
              <TableCell sx={headerCellSx}>Visit Start Date</TableCell>
              <TableCell sx={headerCellSx}>Visit Close Date</TableCell>

              {hasAnyInClinicActivity && (
                <>
                  <TableCell sx={headerCellSx}>In-Clinic Visit Start Date</TableCell>
                  <TableCell sx={headerCellSx}>In-Clinic Visit End Date</TableCell>
                  <TableCell sx={headerCellSx}>In-Clinic Status</TableCell>
                </>
              )}

              {hasAnyInAppActivity && (
                <>
                  <TableCell sx={headerCellSx}>App Visit Start Date</TableCell>
                  <TableCell sx={headerCellSx}>App Visit End Date</TableCell>
                  <TableCell sx={headerCellSx}>App Status</TableCell>
                </>
              )}

              <TableCell sx={headerCellSx}>Overall Status</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                  <Loader />
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              sessions.map((session) => (
                <TableRow key={session.session_id}>
                  <TableCell sx={bodyCellSx}>{session.visit_name}</TableCell>

                  <TableCell sx={bodyCellSx}>{session.visit_type || "Regular"}</TableCell>

                  <TableCell sx={bodyCellSx}>{formatDate(session.visit_open_date)}</TableCell>

                  <TableCell sx={bodyCellSx}>{formatDate(session.visit_close_date)}</TableCell>

                  {hasAnyInClinicActivity && (
                    <>
                      <TableCell sx={bodyCellSx}>
                        {formatDate(session.inclinic_start_date)}
                      </TableCell>

                      <TableCell sx={bodyCellSx}>
                        {formatDate(session.inclinic_completed_date)}
                      </TableCell>

                      <TableCell sx={bodyCellSx}>{session.inclinic_visit_status || "-"}</TableCell>
                    </>
                  )}

                  {hasAnyInAppActivity && (
                    <>
                      <TableCell sx={bodyCellSx}>{formatDate(session.inapp_start_date)}</TableCell>

                      <TableCell sx={bodyCellSx}>
                        {formatDate(session.inapp_completed_date)}
                      </TableCell>

                      <TableCell sx={bodyCellSx}>{session.inapp_visit_status || "-"}</TableCell>
                    </>
                  )}

                  <TableCell sx={bodyCellSx}>{session.overall_visit_status || "-"}</TableCell>
                </TableRow>
              ))}

            {!isLoading && sessions.length === 0 && (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No visit sessions found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ManuallyMarkVisit;
